/**
 * Coups de pouce, Attendus 1218 « multiplication de puissances d'exposant
 * positif d'un même nombre » (2³ × 2⁴ = 2⁷) et 1219 « multiplication de
 * puissances de même exposant positif de deux nombres » (2³ × 5³ = 10³),
 * 3e, Puissances (l. 744-745), un fichier gen_1218 à deux jeux de clés.
 * Seconde passe 3e (2026-09-13). Une page STATIQUE par attendu.
 *
 * L'image que l'élève a déjà : la définition (1217, le facteur répété et
 * compté). Les deux pages font le MÊME geste, « j'écris tous les
 * facteurs », puis je regarde ce que je peux regrouper :
 *   1218 : trois 2 puis quatre 2, sept 2 en tout, 2⁷ ; 3 + 4 = 7, la base
 *          reste 2 (contre 4⁷ et 2¹², les deux règles croisées) ;
 *   1219 : trois 2 et trois 5, trois paires 2 × 5 = 10, 10³ ; le petit 3
 *          ne change pas (contre 10⁶ et 7³).
 *
 * SACRIFIÉS (gen_1218, auto-testés par gen1218.self.test.js) : (2 ; 3, 4)
 * en même base, (2, 5 ; 3) en même exposant.
 */
import { svg } from './1054';

const ORANGE = '#EA580C';
const BLEU = '#2563EB';
const ENCRE = '#1E293B';
const GRIS = '#64748B';

const ligne = (y, contenu, taille = 12, couleur = ENCRE) => `<text x="200" y="${y}" font-size="${taille}" text-anchor="middle" fill="${couleur}">${contenu}</text>`;
const puiss = (x, y, base, exp, couleurBase, couleurExp, taille = 22) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleurBase}">${base}<tspan font-size="${Math.round(taille * 0.6)}" fill="${couleurExp}" dy="-${Math.round(taille * 0.45)}">${exp}</tspan></text>`;
/** Une suite de facteurs « 2 × 2 × 2 » centrée en xCentre, d'une couleur. */
const facteurs = (xCentre, y, liste, couleur, taille = 15) => {
    const pas = 22;
    const x0 = xCentre - ((liste.length - 1) * pas) / 2;
    return liste.map((f, k) => `<text x="${x0 + k * pas}" y="${y}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${f}</text>${k < liste.length - 1 ? `<text x="${x0 + k * pas + pas / 2}" y="${y}" font-size="12" text-anchor="middle" fill="${GRIS}">×</text>` : ''}`).join('\n  ');
};
const accolade = (x1, x2, y, couleur, texte) => `<path d="M ${x1} ${y} Q ${x1} ${y + 8} ${x1 + 6} ${y + 8} L ${x2 - 6} ${y + 8} Q ${x2} ${y + 8} ${x2} ${y}" fill="none" stroke="${couleur}" stroke-width="1.2"/>
  <text x="${(x1 + x2) / 2}" y="${y + 22}" font-size="11" text-anchor="middle" fill="${couleur}">${texte}</text>`;

// ─── 1218 : 2³ × 2⁴ = 2⁷ ───────────────────────────────────────────────────

const SVG_MEME_BASE = svg(
    '2 exposant 3 fois 2 exposant 4. J’écris tous les facteurs : 2 fois 2 fois 2, en bleu, puis 2 fois 2 fois 2 fois 2, en orange. Sept 2 en tout, 2 multiplié 7 fois s’écrit 2 exposant 7. 3 plus 4 égale 7, les exposants s’ajoutent, la base reste 2.',
    `<!-- 1. l'écriture en puissances -->
  ${puiss(150, 44, 2, 3, BLEU, BLEU)}
  <text x="184" y="44" font-size="18" text-anchor="middle" fill="${ENCRE}">×</text>
  ${puiss(218, 44, 2, 4, ORANGE, ORANGE)}

  <!-- 2. tous les facteurs écrits -->
  ${facteurs(112, 92, [2, 2, 2], BLEU)}
  <text x="156" y="92" font-size="14" text-anchor="middle" fill="${ENCRE}">×</text>
  ${facteurs(233, 92, [2, 2, 2, 2], ORANGE)}
  ${accolade(90, 134, 100, BLEU, 'trois 2')}
  ${accolade(200, 266, 100, ORANGE, 'quatre 2')}

  <!-- 3. le résultat -->
  <text x="246" y="164" font-size="14" text-anchor="end" fill="${ENCRE}">2 multiplié 7 fois s’écrit :</text>
  ${puiss(270, 164, 2, 7, ENCRE, ENCRE, 20)}

  <!-- 4. le geste -->
  ${ligne(198, '3 + 4 = 7 : les exposants s’ajoutent. La base reste 2.', 12)}
  ${ligne(220, 'Ce n’est ni 4⁷ ni 2¹².', 12, GRIS)}`,
    236,
);

// ─── 1219 : 2³ × 5³ = 10³ ──────────────────────────────────────────────────

const PAIRES = [104, 200, 296].map((x) => `<rect x="${x - 34}" y="120" width="68" height="26" rx="6" fill="none" stroke="${GRIS}" stroke-width="1"/>
  <text x="${x}" y="138" font-size="14" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">2</tspan> × <tspan fill="${ORANGE}">5</tspan></text>`).join('\n  ');
const ENTRE_PAIRES = [152, 248].map((x) => `<text x="${x}" y="138" font-size="14" text-anchor="middle" fill="${ENCRE}">×</text>`).join('\n  ');

const SVG_MEME_EXPOSANT = svg(
    '2 exposant 3 fois 5 exposant 3. J’écris tous les facteurs : 2 fois 2 fois 2, en bleu, et 5 fois 5 fois 5, en orange. Je regroupe par paires : 2 fois 5, 2 fois 5, 2 fois 5, trois paires qui font 10. Le résultat : 10 exposant 3. Les bases se multiplient, le petit 3 ne change pas.',
    `<!-- 1. l'écriture en puissances -->
  ${puiss(150, 44, 2, 3, BLEU, ENCRE)}
  <text x="184" y="44" font-size="18" text-anchor="middle" fill="${ENCRE}">×</text>
  ${puiss(218, 44, 5, 3, ORANGE, ENCRE)}

  <!-- 2. tous les facteurs, puis les paires -->
  ${facteurs(134, 92, [2, 2, 2], BLEU)}
  <text x="178" y="92" font-size="14" text-anchor="middle" fill="${ENCRE}">×</text>
  ${facteurs(222, 92, [5, 5, 5], ORANGE)}
  ${PAIRES}
  ${ENTRE_PAIRES}

  <!-- 3. le résultat -->
  <text x="176" y="180" font-size="14" text-anchor="middle" fill="${ENCRE}">trois paires 2 × 5 = 10 :</text>
  ${puiss(268, 180, 10, 3, ENCRE, ENCRE, 20)}

  <!-- 4. le geste -->
  ${ligne(212, '2 × 5 = 10 : les bases se multiplient. Le petit 3 ne change pas.', 12)}
  ${ligne(234, 'Ce n’est ni 10⁶ ni 7³.', 12, GRIS)}`,
    250,
);

const AIDE_MEME_BASE = { pages: [{ titre: 'J’écris tous les facteurs', svg: SVG_MEME_BASE }] };
const AIDE_MEME_EXPOSANT = { pages: [{ titre: 'Je regroupe par paires', svg: SVG_MEME_EXPOSANT }] };

const CP_1218 = {
    flash_texte_trou_saisie: AIDE_MEME_BASE,
    puissance_qcm_unique: AIDE_MEME_BASE,
    sens_tableau_vf: AIDE_MEME_BASE,
};
/** 1219 : les clés meme_exposant_* de gen_1218, servies par gen_1219. */
export const CP_1219 = {
    meme_exposant_flash_texte_trou_saisie: AIDE_MEME_EXPOSANT,
    meme_exposant_qcm_unique: AIDE_MEME_EXPOSANT,
    meme_exposant_tableau_vf: AIDE_MEME_EXPOSANT,
};

export default CP_1218;
